/**
 * @file TransferItemsList.tsx
 * @description Позиции документа «Перемещение»: карточки на `<lg`, таблица на
 * `≥lg` (phase19.23 этап B; единый шаблон документа — phase19.24 этап 1,
 * шаг 1).
 *
 * Колонки по утверждённому макету: «Партия», «Откуда → Куда», «Кол-во».
 * Контролы строки (выбор ячейки, количество) живут в `TransferItemCells` и
 * одинаковы в таблице и в карточке — иначе правка на телефоне и на десктопе
 * расходились бы по поведению.
 *
 * `highlightedId` — строка, к которой увёл «Подбор» («уже добавлена»):
 * подсвечиваем тоном `selected` и в таблице, и в карточке.
 *
 * Перемещение держит в строке два живых контрола («Куда» и «Кол-во»), поэтому
 * его таблице нужна большая минимальная ширина: на 1280px рамка скроллится
 * по горизонтали, зато комбобокс ячейки остаётся кликабельным.
 */

import { Trash2 } from "lucide-react";

import { TransferQtyCell, TransferTargetCell } from "./TransferItemCells";
import { transferLotLabel } from "./transfer-row-text";
import { TransferItemRow } from "./TransferItemRow";
import { DocumentItemsTable, type DocumentTableColumn } from "../../ui/DocumentItemsTable";
import { RowActionsMenu, type RowAction } from "../../ui/RowActionsMenu";
import { WmsSkuThumb } from "../WmsSkuThumb";
import type { StorageBinDto } from "../../../lib/api-bins";
import type {
  TransferItemDto,
  UpdateTransferItemPayload,
} from "../../../lib/api-transfers";
import { formatSkuName } from "../../../lib/api-wms";

interface Props {
  items: TransferItemDto[];
  /**
   * Чем заменить «Строк пока нет…» — лист размещения из приёмки без партий
   * объясняет причину сам (`unplaceableItemsText`). `null` — текст по умолчанию.
   */
  emptyText?: string | null;
  /** Строка, к которой увёл «Подбор»: подсветка на пару секунд. */
  highlightedId?: string | null;
  /** `data-row-id` строки для прокрутки. */
  rowDomId?: (itemId: string) => string;
  fromWarehouseId: string;
  bins: StorageBinDto[];
  editable: boolean;
  busy: boolean;
  onUpdate: (itemId: string, payload: UpdateTransferItemPayload) => void;
  onRemove: (itemId: string) => void;
}

export function TransferItemsList({
  items,
  emptyText = null,
  highlightedId = null,
  rowDomId,
  fromWarehouseId,
  bins,
  editable,
  busy,
  onUpdate,
  onRemove,
}: Props) {
  if (items.length === 0) {
    return (
      <div className="rounded-xl bg-surface-lowest px-4 py-8 text-center text-sm text-on-surface-variant ring-ghost">
        {emptyText ??
          "Строк пока нет. Добавьте партию через панель справа или создайте перемещение из проведённой приёмки."}
      </div>
    );
  }

  // Номер строки нужен подписи «Заполните «Куда» в строках: 3, 5» — считаем
  // его один раз здесь, чтобы таблица и карточки называли строку одинаково.
  const numbers = new Map(items.map((item, index) => [item.id, index + 1]));

  function rowActions(item: TransferItemDto) {
    const index = numbers.get(item.id) ?? 0;
    const actions: RowAction[] = editable
      ? [
          {
            key: "remove",
            label: "Удалить строку",
            icon: Trash2,
            danger: true,
            disabled: busy,
            run: () => onRemove(item.id),
          },
        ]
      : [];
    return (
      <RowActionsMenu
        actions={actions}
        title={`Действия со строкой ${index}`}
        ariaLabel={`Действия со строкой ${index}`}
        disabled={busy}
      />
    );
  }

  const columns: DocumentTableColumn<TransferItemDto>[] = [
    {
      key: "lot",
      label: "Партия",
      render: (item) => (
        <span className="font-mono text-xs text-on-surface-variant">
          {transferLotLabel(item)}
        </span>
      ),
    },
    {
      key: "route",
      label: "Откуда → Куда",
      render: (item) => (
        <TransferTargetCell
          item={item}
          fromWarehouseId={fromWarehouseId}
          bins={bins}
          editable={editable}
          busy={busy}
          onUpdate={onUpdate}
        />
      ),
    },
    {
      key: "qty",
      label: "Кол-во",
      width: "96px",
      align: "right",
      render: (item) => (
        <TransferQtyCell
          item={item}
          fromWarehouseId={fromWarehouseId}
          editable={editable}
          busy={busy}
          onUpdate={onUpdate}
        />
      ),
    },
  ];

  return (
    <>
      <ul className="flex flex-col gap-2 lg:hidden">
        {items.map((item) => (
          <li key={item.id} data-row-id={rowDomId?.(item.id)}>
            <TransferItemRow
              item={item}
              index={numbers.get(item.id) ?? 0}
              highlighted={highlightedId === item.id}
              fromWarehouseId={fromWarehouseId}
              bins={bins}
              editable={editable}
              busy={busy}
              onUpdate={onUpdate}
              onRemove={onRemove}
            />
          </li>
        ))}
      </ul>

      <DocumentItemsTable
        items={items}
        rowKey={(item) => item.id}
        rowId={rowDomId ? (item) => rowDomId(item.id) : undefined}
        rowTone={(item) => (highlightedId === item.id ? "selected" : "default")}
        minWidth="680px"
        columns={columns}
        media={() => <WmsSkuThumb />}
        title={(item) => (
          <>
            <span className="text-on-surface-variant">
              {numbers.get(item.id)}.{" "}
            </span>
            {formatSkuName(item.sku)}
          </>
        )}
        code={(item) => item.skuCode}
        actions={rowActions}
      />
    </>
  );
}
